import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from '@emotion/styled';
import { Bell, CheckCircle, AlertTriangle, FileText, User, Clock, ArrowRight, X } from 'lucide-react';
import { colors } from '../theme/colors';

const InboxContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const NotificationDot = styled.button`
  position: relative;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: none;
  background: #f3f4f6;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: #e5e7eb;
    transform: scale(1.05);
  }
  
  &:active {
    transform: scale(0.95);
  }
`;

const NotificationBadge = styled.div`
  position: absolute;
  top: -2px;
  right: -2px;
  min-width: 20px;
  height: 20px;
  background: #f59e0b;
  color: white;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: 600;
  border: 2px solid white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const InboxDropdown = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 0.5rem;
  width: 380px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
  border: 1px solid #e5e7eb;
  z-index: 1000;
  overflow: hidden;
  animation: slideDown 0.2s ease-out;
  
  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const InboxHeader = styled.div`
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #e5e7eb;
  background: #f9fafb;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const InboxTitle = styled.h3`
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: #374151;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const CloseButton = styled.button`
  width: 24px;
  height: 24px;
  border: none;
  background: none;
  color: #6b7280;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.2s ease;
  
  &:hover {
    background: #e5e7eb;
    color: #374151;
  }
`;

const TaskList = styled.div`
  max-height: 400px;
  overflow-y: auto;
`;

const TaskItem = styled.div`
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #f3f4f6;
  transition: background-color 0.2s ease;
  cursor: pointer;
  
  &:hover {
    background: #f9fafb;
  }
  
  &:last-child {
    border-bottom: none;
  }
`;

const TaskHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin-bottom: 0.5rem;
`;

const TaskIcon = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: ${props => props.bgColor};
  color: white;
`;

const TaskContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const TaskTitle = styled.h4`
  margin: 0 0 0.25rem 0;
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
  line-height: 1.4;
`;

const TaskDescription = styled.p`
  margin: 0 0 0.5rem 0;
  font-size: 0.75rem;
  color: #6b7280;
  line-height: 1.4;
`;

const TaskMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  font-size: 0.75rem;
  color: #9ca3af;
`;

const TaskAction = styled.button`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.375rem 0.75rem;
  background: #000000;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: #333333;
    transform: translateX(2px);
  }
`;

const EmptyState = styled.div`
  padding: 2rem 1.5rem;
  text-align: center;
  color: #6b7280;
`;

const EmptyIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: #f3f4f6;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1rem auto;
  color: #9ca3af;
`;

const EmptyTitle = styled.h4`
  margin: 0 0 0.5rem 0;
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
`;

const EmptyDescription = styled.p`
  margin: 0;
  font-size: 0.75rem;
  color: #6b7280;
`;

const TodoInbox = () => {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  
  // Dummy tasks - realistic for a loan processing application
  const tasks = [
    {
      id: 1,
      type: 'validation',
      title: 'Review Loan Analysis #1048084238',
      description: 'Document analysis completed - review extracted values and field matches for accuracy',
      priority: 'high',
      assignedBy: 'Sarah Chen',
      dueDate: '2 hours ago',
      loanId: '1048084238',
      actionText: 'View Analysis',
      icon: CheckCircle,
      bgColor: '#059669',
      navigateTo: '/document-analysis/1048084238'
    },
    {
      id: 2,
      type: 'review',
      title: 'Configure Field Settings',
      description: 'Field matching configuration needs review - some fields may need priority adjustments',
      priority: 'medium',
      assignedBy: 'Mike Rodriguez',
      dueDate: '1 day ago',
      loanId: null,
      actionText: 'Manage Fields',
      icon: AlertTriangle,
      bgColor: '#dc2626',
      navigateTo: '/system-files?tab=fields'
    },
    {
      id: 3,
      type: 'document',
      title: 'Missing Documentation',
      description: 'Loan #1048084239 requires additional property appraisal documents',
      priority: 'medium',
      assignedBy: 'Jennifer Liu',
      dueDate: '3 hours ago',
      loanId: '1048084239',
      actionText: 'Upload Docs',
      icon: FileText,
      bgColor: '#7c3aed'
    },
    {
      id: 4,
      type: 'approval',
      title: 'Final Approval Required',
      description: 'Loan #1048084240 is ready for final approval - all checks completed',
      priority: 'high',
      assignedBy: 'David Park',
      dueDate: '30 minutes ago',
      loanId: '1048084240',
      actionText: 'Approve Loan',
      icon: User,
      bgColor: '#2563eb'
    }
  ];

  const handleTaskAction = (task) => {
    console.log(`Action clicked for task: ${task.title}`);
    
    // Close the inbox first
    setIsOpen(false);
    
    // Navigate to the appropriate screen if navigation is defined
    if (task.navigateTo) {
      navigate(task.navigateTo);
    } else {
      // For tasks without specific navigation, just log for now
      console.log(`No navigation defined for task: ${task.title}`);
    }
  };

  const totalTasks = tasks.length;

  return (
    <InboxContainer>
      <NotificationDot onClick={() => setIsOpen(!isOpen)}>
        <Bell size={20} color={colors.primary} />
        {totalTasks > 0 && (
          <NotificationBadge>
            {totalTasks > 9 ? '9+' : totalTasks}
          </NotificationBadge>
        )}
      </NotificationDot>

      {isOpen && (
        <InboxDropdown>
          <InboxHeader>
            <InboxTitle>
              <Bell size={16} color={colors.primary} />
              Tasks & Notifications
            </InboxTitle>
            <CloseButton onClick={() => setIsOpen(false)}>
              <X size={16} color={colors.primary} />
            </CloseButton>
          </InboxHeader>

          <TaskList>
            {tasks.length > 0 ? (
              tasks.map((task) => {
                const IconComponent = task.icon;
                return (
                  <TaskItem key={task.id} onClick={() => handleTaskAction(task)}>
                    <TaskHeader>
                      <TaskIcon bgColor={task.bgColor}>
                        <IconComponent size={16} />
                      </TaskIcon>
                      <TaskContent>
                        <TaskTitle>{task.title}</TaskTitle>
                        <TaskDescription>{task.description}</TaskDescription>
                        <TaskMeta>
                          <span>Assigned by {task.assignedBy}</span>
                          <span>•</span>
                          <span>{task.dueDate}</span>
                        </TaskMeta>
                      </TaskContent>
                    </TaskHeader>
                    <TaskAction onClick={(e) => {
                      e.stopPropagation();
                      handleTaskAction(task);
                    }}>
                      {task.actionText}
                      <ArrowRight size={12} color="white" />
                    </TaskAction>
                  </TaskItem>
                );
              })
            ) : (
              <EmptyState>
                <EmptyIcon>
                  <CheckCircle size={24} color={colors.primary} />
                </EmptyIcon>
                <EmptyTitle>All caught up!</EmptyTitle>
                <EmptyDescription>
                  You have no pending tasks or notifications.
                </EmptyDescription>
              </EmptyState>
            )}
          </TaskList>
        </InboxDropdown>
      )}
    </InboxContainer>
  );
};

export default TodoInbox;
